import type { AnalyticsConfig, AnalyticsEvent, PageViewEvent, SessionData } from '../types';
import {
  generateSessionId,
  isBrowser,
  isDoNotTrackEnabled,
  getViewport,
  getScreenResolution,
  safeJsonParse,
} from './utils';

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const SESSION_STORAGE_KEY = 'cosmic_analytics_session';

export class CosmicAnalytics {
  private projectId: string;
  private apiEndpoint: string;
  private config: AnalyticsConfig;
  private session: SessionData | null = null;
  private pageViewStart: number = 0;
  private isEnabled: boolean = true;
  private queue: AnalyticsEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(config?: AnalyticsConfig) {
    // Merge default config with user config first
    this.config = {
      apiEndpoint: 'https://api.cosmic.new/analytics',
      debug: false,
      enabled: true,
      respectDoNotTrack: true,
      sendInDevelopment: false,
      ...config,
    };
    
    // Get project ID from config or environment variable
    this.projectId = this.config.projectId || this.getProjectId();
    
    this.apiEndpoint = this.config.apiEndpoint!;
    
    // Check if analytics should be enabled
    this.isEnabled = this.shouldEnableAnalytics();
    
    if (this.isEnabled && isBrowser()) {
      this.initializeSession();
      this.setupEventListeners();
      this.startFlushTimer();
    }
  }

  private getProjectId(): string {
    // Simply return empty string as fallback
    // The projectId should be provided via config from the React component
    return '';
  }

  private isDevelopmentMode(): boolean {
    // First check if isDevelopment is explicitly set in config
    if (typeof this.config.isDevelopment === 'boolean') {
      return this.config.isDevelopment;
    }
    
    // Check for Node.js environment (Next.js server-side)
    if (typeof (globalThis as any).process !== 'undefined') {
      return (globalThis as any).process.env?.NODE_ENV === 'development';
    }
    
    // Check for browser environment
    if (isBrowser()) {
      // Check Next.js runtime config
      if ((window as any).__NEXT_DATA__?.buildId === 'development') {
        return true;
      }
      
      // Fallback to checking hostname
      const hostname = window.location.hostname;
      return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('.local');
    }
    
    return false;
  }

  private shouldEnableAnalytics(): boolean {
    if (!this.config.enabled) return false;
    if (!this.projectId) {
      if (this.config.debug) {
        console.warn('Cosmic Analytics: NEXT_PUBLIC_CLIENT_ID not found');
      }
      return false;
    }
    
    // Check if we're in development mode and if sendInDevelopment is false (default)
    if (this.isDevelopmentMode() && !this.config.sendInDevelopment) {
      if (this.config.debug) {
        console.log('Cosmic Analytics: Disabled in development mode. Set sendInDevelopment: true to enable.');
      }
      return false;
    }
    
    // Respect Do Not Track if configured
    if (this.config.respectDoNotTrack && isDoNotTrackEnabled()) {
      if (this.config.debug) {
        console.log('Cosmic Analytics: Respecting Do Not Track preference');
      }
      return false;
    }
    
    return true;
  }

  private initializeSession(): void {
    const storedSession = this.getStoredSession();
    
    if (storedSession && this.isSessionValid(storedSession)) {
      this.session = storedSession;
      this.session.lastActivity = Date.now();
    } else {
      this.session = {
        id: generateSessionId(),
        startTime: Date.now(),
        pageViews: 0,
        lastActivity: Date.now(),
      };
    }
    
    this.saveSession();
  }

  private getStoredSession(): SessionData | null {
    if (!isBrowser()) return null;
    
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!stored) return null;
      
      return safeJsonParse<SessionData | null>(stored, null);
    } catch {
      return null;
    }
  }

  private isSessionValid(session: SessionData): boolean {
    const now = Date.now();
    return now - session.lastActivity < SESSION_TIMEOUT;
  }

  private saveSession(): void {
    if (!isBrowser() || !this.session) return;
    
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(this.session));
    } catch (error) {
      if (this.config.debug) {
        console.error('Cosmic Analytics: Failed to save session', error);
      }
    }
  }

  private setupEventListeners(): void {
    if (!isBrowser()) return;
    
    // Page visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.trackPageExit();
        this.flush(true);
      } else if (document.visibilityState === 'visible') {
        // Track a new page view when returning to the tab
        this.trackPageView();
      }
    });
    
    // Before unload
    window.addEventListener('beforeunload', () => {
      this.trackPageExit();
      this.flush(true);
    });
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, 5000); // Flush every 5 seconds
  }

  private stopFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  private addToQueue(event: AnalyticsEvent): void {
    this.queue.push(event);
    
    // Flush immediately if queue is getting large
    if (this.queue.length >= 10) {
      this.flush();
    }
  }

  private async flush(forceSendNow = false): Promise<void> {
    if (this.queue.length === 0) return;
    
    const events = [...this.queue];
    this.queue = [];
    
    try {
      const response = await fetch(`${this.apiEndpoint}/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events }),
        keepalive: forceSendNow,
      });
      
      if (!response.ok && this.config.debug) {
        console.error('Cosmic Analytics: Failed to send events', response.status);
        // Re-add events to queue for retry
        this.queue.unshift(...events);
      }
    } catch (error) {
      if (this.config.debug) {
        console.error('Cosmic Analytics: Error sending events', error);
      }
      // Re-add events to queue for retry
      this.queue.unshift(...events);
    }
  }

  public trackPageView(overrides?: Partial<PageViewEvent>): void {
    if (!this.isEnabled || !isBrowser() || !this.session) return;
    
    this.pageViewStart = Date.now();
    this.session.pageViews++;
    this.session.lastActivity = Date.now();
    this.saveSession();
    
    const event: PageViewEvent = {
      event: 'pageview',
      projectId: this.projectId,
      sessionId: this.session.id,
      url: window.location.href,
      title: document.title,
      referrer: document.referrer || 'direct',
      timestamp: Date.now(),
      screenResolution: getScreenResolution(),
      viewport: getViewport(),
      language: navigator.language,
      userAgent: navigator.userAgent,
      ...overrides,
    };
    
    if (this.config.debug) {
      console.log('Cosmic Analytics: Page view', event);
    }
    
    this.addToQueue(event);
  }

  public trackPageExit(): void {
    if (!this.isEnabled || !isBrowser() || !this.session || !this.pageViewStart) return;
    
    const duration = Date.now() - this.pageViewStart;
    
    const event = {
      event: 'pageexit' as const,
      projectId: this.projectId,
      sessionId: this.session.id,
      url: window.location.href,
      duration,
      timestamp: Date.now(),
    };
    
    if (this.config.debug) {
      console.log('Cosmic Analytics: Page exit', event);
    }
    
    this.addToQueue(event);
  }

  public reset(): void {
    if (!isBrowser()) return;
    
    // Clear session
    this.session = null;
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    
    // Re-initialize with new session
    if (this.isEnabled) {
      this.initializeSession();
    }
  }

  public disable(): void {
    this.isEnabled = false;
    this.stopFlushTimer();
    this.queue = [];
  }

  public enable(): void {
    this.isEnabled = this.shouldEnableAnalytics();
    if (this.isEnabled && isBrowser()) {
      this.initializeSession();
      this.startFlushTimer();
    }
  }
} 